import {
  computeBasePermissions,
  computeChannelPermissions,
  hasPermission,
  highestRolePosition,
  PRIVATE_CHANNEL_PERMISSIONS,
  type Channel,
  type PermissionSubject,
} from '@spawnwire/shared';
import { useStore, type AppState } from '../store/store';

/**
 * Client-side permission math mirrors the server (same shared code) so the UI
 * only offers actions that will succeed. The server still checks every call.
 */
export function subjectFor(state: AppState, spaceId: string, userId?: string): PermissionSubject | null {
  const space = state.spaces[spaceId];
  const me = state.me;
  if (!space || !me) return null;
  const uid = userId ?? me.id;
  const member = uid === me.id ? state.myMembers[spaceId] : state.members[spaceId]?.[uid];
  if (!member) return null;
  const everyone = space.roles.find((r) => r.isDefault);
  return {
    userId: uid,
    ownerId: space.ownerId,
    everyoneRoleId: everyone?.id ?? '',
    roles: space.roles,
    memberRoleIds: member.roleIds,
    timeoutUntil: member.timeoutUntil,
    bot: member.user.bot,
  };
}

export function spacePermissions(state: AppState, spaceId: string): number {
  const subject = subjectFor(state, spaceId);
  return subject ? computeBasePermissions(subject) : 0;
}

export function channelPermissions(state: AppState, channel: Channel | undefined): number {
  if (!channel) return 0;
  if (channel.type === 'dm' || channel.type === 'group_dm') return PRIVATE_CHANNEL_PERMISSIONS;
  let root = channel;
  if (channel.type === 'thread') {
    const parent = channel.parentId ? state.channels[channel.parentId] : undefined;
    if (!parent) return 0;
    if (parent.type === 'dm' || parent.type === 'group_dm') return PRIVATE_CHANNEL_PERMISSIONS;
    root = parent;
  }
  if (!root.spaceId) return 0;
  const subject = subjectFor(state, root.spaceId);
  if (!subject) return 0;
  const category = root.parentId ? state.channels[root.parentId] : undefined;
  return computeChannelPermissions(subject, root.type === 'category' ? [] : (category?.overwrites ?? []), root.overwrites);
}

export function can(perms: number, bit: number): boolean {
  return hasPermission(perms, bit);
}

export function useSpacePermissions(spaceId: string | null | undefined): number {
  return useStore((s) => (spaceId ? spacePermissions(s, spaceId) : 0));
}

export function useChannelPermissions(channelId: string | null | undefined): number {
  return useStore((s) => (channelId ? channelPermissions(s, s.channels[channelId]) : 0));
}

/** Whether the current user outranks another member in a space. */
export function canActOn(state: AppState, spaceId: string, targetId: string): boolean {
  const me = subjectFor(state, spaceId);
  const space = state.spaces[spaceId];
  if (!me || !space || targetId === space.ownerId || targetId === me.userId) return false;
  if (me.userId === space.ownerId) return true;
  const target = subjectFor(state, spaceId, targetId);
  if (!target) return true;
  return highestRolePosition(me) > highestRolePosition(target);
}

export function myTopRolePosition(state: AppState, spaceId: string): number {
  const me = subjectFor(state, spaceId);
  return me ? highestRolePosition(me) : 0;
}
